import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { BRAND, FOUNDERS } from "../mock/mock";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setShown(true), obs.disconnect()),
      { threshold: 0.15 }
    );
    const node = ref.current;
    if (node) obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${shown ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const FounderCard = ({ f, idx }) => {
  return (
    <Reveal delay={idx * 120} className="h-full">
      <article className="h-full flex flex-col border-t border-black/15 pt-8">
        <div className="flex items-baseline justify-between gap-4 mb-3">
          <span className="text-[11px] uppercase tracking-[0.3em] text-black/45">
            0{idx + 1}
          </span>
          <span className="text-[11px] uppercase tracking-[0.24em] text-black/55">
            {f.role}
          </span>
        </div>
        <h4 className="font-display text-5xl md:text-6xl uppercase leading-[0.95]">
          {f.name}
        </h4>
        <div className="mt-6 space-y-4 text-black/70 leading-relaxed text-[15px]">
          {f.bio.split("\n\n").map((para) => (
            <p key={`${f.name}-${para.slice(0, 24)}`}>{para}</p>
          ))}
        </div>
      </article>
    </Reveal>
  );
};

const Founders = () => {
  return (
    <section className="bg-white text-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Header */}
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:items-end mb-14">
            <div className="md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-6">
                Founders
              </p>
              <h3 className="font-display text-[12vw] md:text-[5.6vw] uppercase leading-[0.92]">
                The two who <span className="font-serif-display italic font-black">started</span> it all.
              </h3>
            </div>
            <p className="md:col-span-4 max-w-md text-black/65 leading-relaxed">
              Co-founded with a single belief — outdoor advertising should be
              unmissable, immersive and built for the streets.
            </p>
          </div>
        </Reveal>

        {/* Group photo — full width */}
        <Reveal delay={80}>
          <div className="relative aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden grain mb-12 md:mb-16">
            <img
              src={BRAND.foundersPhoto}
              alt="AMS Founders Sidhanth Bhutani and Om Malhotra"
              className="absolute inset-0 w-full h-full object-cover grayscale"
            />
            <div className="absolute bottom-5 left-5 text-white text-[11px] uppercase tracking-[0.24em] inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Pan India · Founders
            </div>
          </div>
        </Reveal>

        {/* Side-by-side founder bios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {FOUNDERS.map((f, i) => (
            <FounderCard key={f.name} f={f} idx={i} />
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-16 md:mt-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-t border-black/15 pt-10">
            <p className="max-w-xl text-black/65 leading-relaxed">
              Together, Sidhanth and Om continue to push AMS toward becoming a
              leading force in immersive outdoor advertising.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] font-semibold px-6 py-4 border border-black hover:bg-black hover:text-white transition-colors duration-200 w-fit"
            >
              About AMS
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Founders;
